<template>
  <v-toolbar
    :extension-height="extensionHeight"
    :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
    class="filter-toolbar"
    flat
  >
    <template #default>
      <v-btn-toggle
        v-model="timeRange"
        :mandatory="mandatory"
        borderless
        class="transparent"
        color="primary"
        dense
        tile
        @change="onTimeRangeChange"
      >
        <v-btn
          v-if="!hideToday"
          :value="valueToday"
          active-class="primary--text text--darken-1"
          class="rounded ml-1"
          color="grey"
          style="min-width: 24px"
          text
        >
          <v-icon
            :color="timeRange === valueToday ? 'primary darken-1' : 'grey'"
            :left="$vuetify.breakpoint.smAndUp"
            small
          >
            fas fa-calendar-day
          </v-icon>

          <span v-if="$vuetify.breakpoint.smAndUp">
            {{ $t('Common.Date.Today') }}
          </span>
        </v-btn>

        <v-btn
          v-if="!hideWeek"
          :value="valueWeek"
          active-class="primary--text text--darken-1"
          class="rounded ml-1"
          color="grey"
          style="min-width: 24px"
          text
        >
          <v-icon
            :color="timeRange === valueWeek ? 'primary darken-1' : 'grey'"
            :left="$vuetify.breakpoint.smAndUp"
            small
          >
            fas fa-calendar-week
          </v-icon>

          <span v-if="$vuetify.breakpoint.smAndUp">
            {{ $t('Common.Date.Week') }}
          </span>
        </v-btn>

        <v-btn
          v-if="!hideMonth"
          :value="valueMonth"
          active-class="primary--text text--darken-1"
          class="rounded ml-1"
          color="grey"
          style="min-width: 24px"
          text
        >
          <v-icon
            :color="timeRange === valueMonth ? 'primary darken-1' : 'grey'"
            :left="$vuetify.breakpoint.smAndUp"
            small
          >
            fas fa-calendar-alt
          </v-icon>

          <span v-if="$vuetify.breakpoint.smAndUp">
            {{ $t('Common.Date.Month') }}
          </span>
        </v-btn>

        <v-btn
          v-if="!hideThreeMonths"
          :value="valueThreeMonths"
          active-class="primary--text text--darken-1"
          class="rounded ml-1"
          color="grey"
          style="min-width: 24px"
          text
        >
          <v-icon
            :color="timeRange === valueThreeMonths ? 'primary darken-1' : 'grey'"
            :left="$vuetify.breakpoint.smAndUp"
            small
          >
            fas fa-calendar-alt
          </v-icon>

          <span v-if="$vuetify.breakpoint.smAndUp">
            {{ $t('Common.Date.ThreeMonths') }}
          </span>
        </v-btn>

        <v-btn
          v-if="!hideSixMonths"
          :value="valueSixMonths"
          active-class="primary--text text--darken-1"
          class="rounded ml-1"
          color="grey"
          style="min-width: 24px"
          text
        >
          <v-icon
            :color="timeRange === valueSixMonths ? 'primary darken-1' : 'grey'"
            :left="$vuetify.breakpoint.smAndUp"
            small
          >
            fas fa-calendar-alt
          </v-icon>

          <span v-if="$vuetify.breakpoint.smAndUp">
            {{ $t('Common.Date.SixMonths') }}
          </span>
        </v-btn>

        <v-btn
          v-if="!hideYear"
          :value="valueYear"
          active-class="primary--text text--darken-1"
          class="rounded ml-1"
          color="grey"
          style="min-width: 24px"
          text
        >
          <v-icon
            :color="timeRange === valueYear ? 'primary darken-1' : 'grey'"
            :left="$vuetify.breakpoint.smAndUp"
            small
          >
            fas fa-calendar
          </v-icon>

          <span v-if="$vuetify.breakpoint.smAndUp">
            {{ $t('Common.Date.Year') }}
          </span>
        </v-btn>

        <v-btn
          v-if="!hidePeriod"
          :value="valuePeriod"
          active-class="primary--text text--darken-1"
          class="rounded ml-1"
          color="grey"
          style="min-width: 24px"
          text
        >
          <v-icon
            :color="timeRange === valuePeriod ? 'primary darken-1' : 'grey'"
            :left="$vuetify.breakpoint.smAndUp"
            small
          >
            fas fa-calendar-plus
          </v-icon>

          <span v-if="$vuetify.breakpoint.smAndUp">
            {{ $t('Common.Date.Period') }}
          </span>
        </v-btn>
      </v-btn-toggle>

      <v-divider
        v-if="$vuetify.breakpoint.mdAndUp"
        class="mx-2"
        vertical
      />

      <template v-if="$vuetify.breakpoint.mdAndUp && timeRange === valuePeriod">
        <v-menu
          v-show="statsDateFieldsVisible"
          ref="statsStartDateVisible"
          v-model="statsStartDateVisible"
          :close-on-content-click="false"
          left
          max-width="290px"
          min-width="140px"
          offset-y
          style="width: 100%"
          transition="scale-transition"
        >
          <template #activator="{on, attrs}">
            <v-text-field
              v-model="statsStartDateFmt"
              :background-color="isDarkMode ? '' : 'grey lighten-3'"
              :label="$t('Common.Date.PeriodFrom')"
              class="dateTextField ml-2"
              clearable
              flat
              height="36px"
              prepend-inner-icon="event"
              readonly
              single-line
              solo
              v-bind="attrs"
              @blur="statsStartDate = parseDate(statsStartDateFmt)"
              v-on="on"
            />
          </template>

          <v-date-picker
            v-model="statsStartDate"
            :allowed-dates="allowedStartDates"
            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
            first-day-of-week="1"
            scrollable
            @input="statsStartDateFmt = formatDate($event); statsStartDateVisible = false"
          />
        </v-menu>

        <v-menu
          v-show="statsDateFieldsVisible"
          ref="statsEndDateVisible"
          v-model="statsEndDateVisible"
          :close-on-content-click="false"
          left
          max-width="290px"
          min-width="140px"
          offset-y
          style="width: 100%"
          transition="scale-transition"
        >
          <template #activator="{on, attrs}">
            <v-text-field
              v-model="statsEndDateFmt"
              :background-color="isDarkMode ? '' : 'grey lighten-3'"
              :label="$t('Common.Date.PeriodTo')"
              class="dateTextField ml-2 mr-2"
              clearable
              flat
              height="36px"
              hide-details
              prepend-inner-icon="event"
              readonly
              single-line
              solo
              v-bind="attrs"
              @blur="statsEndDate = parseDate(statsEndDateFmt)"
              v-on="on"
            />
          </template>

          <v-date-picker
            v-model="statsEndDate"
            :allowed-dates="allowedEndDates"
            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
            first-day-of-week="1"
            scrollable
            @input="statsEndDateFmt = formatDate($event); statsEndDateVisible = false"
          />
        </v-menu>
      </template>
      <slot />
    </template>

    <template
      v-if="extension || ($vuetify.breakpoint.smAndDown && timeRange === valuePeriod)"
      #extension
    >
      <template v-if="$vuetify.breakpoint.smAndDown && timeRange === valuePeriod">
        <v-menu
          v-show="statsDateFieldsVisible"
          ref="statsStartDateVisible"
          v-model="statsStartDateVisible"
          :close-on-content-click="false"
          left
          max-width="290px"
          min-width="140px"
          offset-y
          style="width: 100%"
          transition="scale-transition"
        >
          <template #activator="{on, attrs}">
            <v-text-field
              v-model="statsStartDateFmt"
              :background-color="isDarkMode ? '' : 'grey lighten-3'"
              :label="$t('Common.Date.PeriodFrom')"
              class="dateTextField ml-2"
              clearable
              flat
              height="36px"
              prepend-inner-icon="event"
              readonly
              single-line
              solo
              v-bind="attrs"
              @blur="statsStartDate = parseDate(statsStartDateFmt)"
              v-on="on"
            />
          </template>

          <v-date-picker
            v-model="statsStartDate"
            :allowed-dates="allowedStartDates"
            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
            first-day-of-week="1"
            scrollable
            @input="statsStartDateFmt = formatDate($event); statsStartDateVisible = false"
          />
        </v-menu>

        <v-menu
          v-show="statsDateFieldsVisible"
          ref="statsEndDateVisible"
          v-model="statsEndDateVisible"
          :close-on-content-click="false"
          left
          max-width="290px"
          min-width="140px"
          offset-y
          style="width: 100%"
          transition="scale-transition"
        >
          <template #activator="{on, attrs}">
            <v-text-field
              v-model="statsEndDateFmt"
              :background-color="isDarkMode ? '' : 'grey lighten-3'"
              :label="$t('Common.Date.PeriodTo')"
              class="dateTextField ml-2 mr-2"
              clearable
              flat
              height="36px"
              hide-details
              prepend-inner-icon="event"
              readonly
              single-line
              solo
              v-bind="attrs"
              @blur="statsEndDate = parseDate(statsEndDateFmt)"
              v-on="on"
            />
          </template>

          <v-date-picker
            v-model="statsEndDate"
            :allowed-dates="allowedEndDates"
            :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
            first-day-of-week="1"
            scrollable
            @input="statsEndDateFmt = formatDate($event); statsEndDateVisible = false"
          />
        </v-menu>
      </template>

      <slot name="extension" />
    </template>
  </v-toolbar>
</template>

<script>

import vModel from '@/mixins/vModel'
import Appdata from '@/mixins/appdata'
import {dateAddMonths, dateSubtractMonths} from '@/lib/helper/helper.js'

export default {
  name      : 'ToolbarPeriodSelector',
  components: {},
  directives: {},
  mixins    : [Appdata, vModel],
  props     : {
    defaultPeriod: {
      type   : String,
      default: 'today'
    },

    valueToday: {
      type   : String,
      default: 'today'
    },
    valueWeek: {
      type   : String,
      default: 'week'
    },
    valueMonth: {
      type   : String,
      default: 'month'
    },
    valueThreeMonths: {
      type   : String,
      default: 'months3'
    },
    valueSixMonths: {
      type   : String,
      default: 'months6'
    },
    valueYear: {
      type   : String,
      default: 'year'
    },
    valuePeriod: {
      type   : String,
      default: 'custom'
    },

    mandatory: {
      type   : Boolean,
      default: true
    },

    hideToday: {
      type   : Boolean,
      default: false
    },
    hideWeek: {
      type   : Boolean,
      default: false
    },
    hideMonth: {
      type   : Boolean,
      default: false
    },
    hideThreeMonths: {
      type   : Boolean,
      default: false
    },
    hideSixMonths: {
      type   : Boolean,
      default: false
    },
    hideYear: {
      type   : Boolean,
      default: false
    },
    hidePeriod: {
      type   : Boolean,
      default: false
    },

    extension: {
      type   : Boolean,
      default: false
    },
    extensionHeight: {
      type   : [Number, String],
      default: 60
    },

    maxMonthsAllowed: {
      type   : Number,
      default: 6
    }
  },
  data() {
    return {
      statsStartDateVisible: false,
      statsEndDateVisible  : false,
      statsStartDate       : '',
      statsStartDateFmt    : '',
      statsEndDate         : '',
      statsEndDateFmt      : '',
      timeRange            : this.defaultPeriod
    }
  },
  computed: {
    statsDateFieldsVisible() {
      return this.timeRange !== this.valueToday && this.timeRange !== this.valueWeek && this.timeRange !== this.valueMonth && this.timeRange !== this.valueYear
    }
  },
  watch: {
    timeRange(newVal) {
      if (newVal !== this.valuePeriod) this.statsStartDateFmt = this.statsStartDate = this.statsEndDateFmt = this.statsEndDate = ''
    },
    statsStartDate(newVal) {
      if (this.statsEndDate && newVal) {
        this.statsDatePeriod = newVal + ' 00:00_' + this.statsEndDate + ' 00:00'
        this.onTimeRangeChange()
      } else {
        this.statsDatePeriod = this.defaultPeriod
      }
    },
    statsEndDate(newVal) {
      if (this.statsStartDate && newVal) {
        this.statsDatePeriod = this.statsStartDate + ' 00:00_' + newVal + ' 00:00'
        this.onTimeRangeChange()
      } else {
        this.statsDatePeriod = this.defaultPeriod
      }
    },
    vModel(newVal) {
      console.log('vModel', newVal)
    }
  },
  created() {
    this.vModel = this.timeRange
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  methods: {
    onTimeRangeChange() {
      if (this.timeRange !== this.valuePeriod) {
        this.vModel = this.timeRange
      } else {
        if (this.statsDatePeriod && this.statsDatePeriod !== this.defaultPeriod) {
          this.vModel = this.statsDatePeriod
        }
      }
    },

    allowedStartDates: function (date) {
      const currDate = new Date(date).getTime()
      const endDate = this.statsEndDate ? new Date(this.statsEndDate).getTime() : null
      const minDate = this.statsEndDate ? dateSubtractMonths(this.statsEndDate, this.maxMonthsAllowed || 12) : null
      return endDate ? (currDate <= endDate && currDate >= minDate.getTime()) : true
    },

    allowedEndDates: function (date) {
      const currDate = new Date(date).getTime()
      const startDate = this.statsStartDate ? new Date(this.statsStartDate).getTime() : null
      const maxDate = this.statsStartDate ? dateAddMonths(this.statsStartDate, this.maxMonthsAllowed || 12) : null
      return startDate ? (currDate >= startDate && currDate <= maxDate.getTime()) : true
    },

    formatDate(date) {
      if (!date) {
        return null
      }

      const [year, month, day] = date.split('-')
      return `${day}-${month}-${year}`
    },

    parseDate(date) {
      if (!date) {
        return null
      }

      const [day, month, year] = date.split('-')
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
:deep(.dateTextField) {
  max-width: 170px;
}

:deep(.dateTextField .v-input__prepend-outer) {
  margin-top: 8px;
}

:deep(.dateTextField .v-input__control) {
  line-height: 36px !important;
  height: 36px !important;
  min-height: 36px !important;
}
</style>
